import THREE from "three";
import SliderControl from "./SliderControl"
import SiteManagerView from "@/Site/SiteManagerView"
import { MeshTransform, OrientationRangeViewModel, MovableModel } from "@/TemsManagerWebInterface"
import TemsWebEvents from "@/TemsWebEvents"
import Localization from "@/Localization"
import { TemsObject3D } from "@/CommonTypes"
import _ from "lodash"

export default class PositionControl {

    private content: JQuery;
    private siteManagerView: SiteManagerView; // TODO would be nice to get rid of this thing
    private deviceId: string;

    private sliderPosX: SliderControl;
    private sliderPosY: SliderControl;
    private sliderPosZ: SliderControl;
    private sliderRotX: SliderControl;
    private sliderRotY: SliderControl;
    private sliderRotZ: SliderControl;

    private forceUpdate = false;
    private destroyed = false;

    public get Content() { return this.content; }

    public get Orientation(): MeshTransform {
        return {
            PositionX: this.sliderPosX.getValue(),
            PositionY: this.sliderPosY.getValue(),
            PositionZ: this.sliderPosZ.getValue(),
            AngleX: THREE.Math.degToRad(this.sliderRotX.getValue()),
            AngleY: THREE.Math.degToRad(this.sliderRotY.getValue()),
            AngleZ: THREE.Math.degToRad(this.sliderRotZ.getValue()),
            ScaleX: 1.0,
            ScaleY: 1.0,
            ScaleZ: 1.0
        };
    }
    private meshUpdatedHandler = (evt, meshInfo, source: string) => {
        if (meshInfo.meshInfo.Id === this.deviceId) {
            this.updateFromMesh(meshInfo, source === "mousemoved");
        }
    }

    constructor(deviceModel: MovableModel, siteManagerView: SiteManagerView) {
        this.siteManagerView = siteManagerView;
        this.createPositionControl(deviceModel);
        this.deviceId = deviceModel.Id;

        TemsWebEvents.subscribeEvent(TemsWebEvents.MeshUpdated, this.meshUpdatedHandler);
    }

    public destroy() {
        TemsWebEvents.unsubscribeEvent(TemsWebEvents.MeshUpdated, this.meshUpdatedHandler);
    }

    public updateDeviceModel(deviceModel: MovableModel) {
        this.updateOrientationRanges(deviceModel.OrientationRanges);
    }


    public updateOrientationRanges(orientationRanges: OrientationRangeViewModel[]) {
        const limits = this.getSliderLimitsFromOrientationRanges(orientationRanges);

        this.sliderPosX.updateSliderAttribute("max", limits.MaxCoordinateX);
        this.sliderPosY.updateSliderAttribute("max", limits.MaxCoordinateY);
        this.sliderPosZ.updateSliderAttribute("max", limits.MaxCoordinateZ);
        this.sliderPosX.updateSliderAttribute("min", limits.MinCoordinateX);
        this.sliderPosY.updateSliderAttribute("min", limits.MinCoordinateY);
        this.sliderPosZ.updateSliderAttribute("min", limits.MinCoordinateZ);

        this.sliderRotX.updateSliderAttribute("max", limits.MaxAngleX);
        this.sliderRotY.updateSliderAttribute("max", limits.MaxAngleY);
        this.sliderRotZ.updateSliderAttribute("max", limits.MaxAngleZ);
        this.sliderRotX.updateSliderAttribute("min", limits.MinAngleX);
        this.sliderRotY.updateSliderAttribute("min", limits.MinAngleY);
        this.sliderRotZ.updateSliderAttribute("min", limits.MinAngleZ);
    }

    public forceSliderUpdate() {
        this.sliderPosX.forceSliderUpdate();
        this.sliderPosY.forceSliderUpdate();
        this.sliderPosZ.forceSliderUpdate();
        this.sliderPosX.forceSliderUpdate();
        this.sliderPosY.forceSliderUpdate();
        this.sliderPosZ.forceSliderUpdate();
    }

    private createPositionControl(deviceModel: MovableModel) {
        this.content = $("<div>", { "class": "positionControl" });
        const limits = this.getSliderLimitsFromOrientationRanges(deviceModel.OrientationRanges);

        const location = $("<span>", { "class": "container-fluid flexGrow1" });
        location.append(`<div>${Localization.resourceStrings.SiteView_LabelPosition}</div>`);
        this.sliderPosX = new SliderControl("X", limits.MinCoordinateX, limits.MaxCoordinateX, 0.001, "m", (id, newValue) => { this.forceUpdate = true; this.siteManagerView.siteView.updateMeshPositionX(id, newValue); }, deviceModel.Id, `sitetree-position-x-${deviceModel.Id}`, this.siteManagerView.IsInEditMode);
        this.sliderPosY = new SliderControl("Y", limits.MinCoordinateY, limits.MaxCoordinateY, 0.001, "m", (id, newValue) => { this.forceUpdate = true; this.siteManagerView.siteView.updateMeshPositionY(id, newValue); }, deviceModel.Id, `sitetree-position-y-${deviceModel.Id}`, this.siteManagerView.IsInEditMode);
        this.sliderPosZ = new SliderControl("Z", limits.MinCoordinateZ, limits.MaxCoordinateZ, 0.001, "m", (id, newValue) => { this.forceUpdate = true; this.siteManagerView.siteView.updateMeshPositionZ(id, newValue); }, deviceModel.Id, `sitetree-position-z-${deviceModel.Id}`, this.siteManagerView.IsInEditMode);
        location.append(
            $("<div>", { class: "row" })
                .append($("<div>", { class: "col-xs-2  col-sm-1", html: this.sliderPosX.Label }))
                .append($("<div>", { class: "col-xs-10 col-sm-3 col-sliderValue", html: this.sliderPosX.Value }))
                .append($("<div>", { class: "col-xs-12 col-sm-7", html: this.sliderPosX.Slider })));
        location.append(
            $("<div>", { class: "row" })
                .append($("<div>", { class: "col-xs-2  col-sm-1", html: this.sliderPosY.Label }))
                .append($("<div>", { class: "col-xs-10 col-sm-3 col-sliderValue", html: this.sliderPosY.Value }))
                .append($("<div>", { class: "col-xs-12 col-sm-7", html: this.sliderPosY.Slider })));
        location.append(
            $("<div>", { class: "row" })
                .append($("<div>", { class: "col-xs-2  col-sm-1", html: this.sliderPosZ.Label }))
                .append($("<div>", { class: "col-xs-10 col-sm-3 col-sliderValue", html: this.sliderPosZ.Value }))
                .append($("<div>", { class: "col-xs-12 col-sm-7", html: this.sliderPosZ.Slider })));

        const rotation = $("<span>", { "class": "container-fluid flexGrow1" });
        rotation.append(`<div>${Localization.resourceStrings.SiteView_LabelRotation}</div>`);
        this.sliderRotX = new SliderControl("X", limits.MinAngleX, limits.MaxAngleX, 0.05, "°", (id, newValue) => { this.forceUpdate = true; this.siteManagerView.siteView.updateMeshRotationX(id, newValue); }, deviceModel.Id, `sitetree-rotation-x-${deviceModel.Id}`, this.siteManagerView.IsInEditMode);
        this.sliderRotY = new SliderControl("Y", limits.MinAngleY, limits.MaxAngleY, 0.05, "°", (id, newValue) => { this.forceUpdate = true; this.siteManagerView.siteView.updateMeshRotationY(id, newValue); }, deviceModel.Id, `sitetree-rotation-y-${deviceModel.Id}`, this.siteManagerView.IsInEditMode);
        this.sliderRotZ = new SliderControl("Z", limits.MinAngleZ, limits.MaxAngleZ, 0.05, "°", (id, newValue) => { this.forceUpdate = true; this.siteManagerView.siteView.updateMeshRotationZ(id, newValue); }, deviceModel.Id, `sitetree-rotation-z-${deviceModel.Id}`, this.siteManagerView.IsInEditMode);
        rotation.append(
            $("<div>", { class: "row" })
                .append($("<div>", { class: "col-xs-2  col-sm-1", html: this.sliderRotX.Label }))
                .append($("<div>", { class: "col-xs-10 col-sm-3 col-sliderValue", html: this.sliderRotX.Value }))
                .append($("<div>", { class: "col-xs-12 col-sm-7", html: this.sliderRotX.Slider })));
        rotation.append(
            $("<div>", { class: "row" })
                .append($("<div>", { class: "col-xs-2  col-sm-1", html: this.sliderRotY.Label }))
                .append($("<div>", { class: "col-xs-10 col-sm-3 col-sliderValue", html: this.sliderRotY.Value }))
                .append($("<div>", { class: "col-xs-12 col-sm-7", html: this.sliderRotY.Slider })));
        rotation.append(
            $("<div>", { class: "row" })
                .append($("<div>", { class: "col-xs-2  col-sm-1", html: this.sliderRotZ.Label }))
                .append($("<div>", { class: "col-xs-10 col-sm-3 col-sliderValue", html: this.sliderRotZ.Value }))
                .append($("<div>", { class: "col-xs-12 col-sm-7", html: this.sliderRotZ.Slider })));

        this.content.append(location);
        this.content.append(rotation);
    }

    private getSliderLimitsFromOrientationRanges(ranges: OrientationRangeViewModel[]): OrientationRangeViewModel {

        // initialze with default values
        var limits: OrientationRangeViewModel = {
            MinAngleX: -180,
            MinAngleY: -180,
            MinAngleZ: -180,
            MaxAngleX: 180,
            MaxAngleY: 180,
            MaxAngleZ: 180,
            MinCoordinateX: -10,
            MinCoordinateY: -10,
            MinCoordinateZ: -10,
            MaxCoordinateX: 10,
            MaxCoordinateY: 10,
            MaxCoordinateZ: 10
        };

        if (ranges.length > 0) {
            limits.MinAngleX = this.getExtremeValue("min", ranges, item => { return item.MinAngleX; });
            limits.MinAngleY = this.getExtremeValue("min", ranges, item => { return item.MinAngleY; });
            limits.MinAngleZ = this.getExtremeValue("min", ranges, item => { return item.MinAngleZ; });
            limits.MaxAngleX = this.getExtremeValue("max", ranges, item => { return item.MaxAngleX; });
            limits.MaxAngleY = this.getExtremeValue("max", ranges, item => { return item.MaxAngleY; });
            limits.MaxAngleZ = this.getExtremeValue("max", ranges, item => { return item.MaxAngleZ; });
            limits.MinCoordinateX = this.getExtremeValue("min", ranges, item => { return item.MinCoordinateX; });
            limits.MinCoordinateY = this.getExtremeValue("min", ranges, item => { return item.MinCoordinateY; });
            limits.MinCoordinateZ = this.getExtremeValue("min", ranges, item => { return item.MinCoordinateZ; });
            limits.MaxCoordinateX = this.getExtremeValue("max", ranges, item => { return item.MaxCoordinateX; });
            limits.MaxCoordinateY = this.getExtremeValue("max", ranges, item => { return item.MaxCoordinateY; });
            limits.MaxCoordinateZ = this.getExtremeValue("max", ranges, item => { return item.MaxCoordinateZ; });
        }

        return limits;
    }

    private getExtremeValue(option: string, array: any[], mappingFunction: (item: OrientationRangeViewModel, index: number) => number): number {
        var mappedArray = $.map(array, mappingFunction);
        var min: number;
        var max: number;

        $.each(mappedArray, (index, value) => {
            min = min === undefined || value < min ? value : min;
            max = max === undefined || value > max ? value : max;
        });

        if (option === "min") {
            return min;
        } else if (option === "max") {
            return max;
        } else {
            // this should not happen
            return undefined;
        }
    }

    updateFromMesh(meshObject: TemsObject3D, limitToOrientationRanges: boolean) {
        var posChanged = this.initializeFromMesh(meshObject, limitToOrientationRanges);
        if (posChanged || this.forceUpdate) {
            TemsWebEvents.publishEvent(TemsWebEvents.DevicePositionChanging, [this.deviceId]);
            this.forceUpdate = false;
        }
    }

    // Returns true if an update occured
    private initializeFromMesh(meshObject: TemsObject3D, limitToOrientationRanges: boolean) : boolean {
        const transform = meshObject.meshInfo.Transform;

        if (isNaN(transform.PositionX) ||
            isNaN(transform.PositionY) ||
            isNaN(transform.PositionZ) ||
            isNaN(transform.AngleX) ||
            isNaN(transform.AngleY) ||
            isNaN(transform.AngleZ)) {
            console.debug("Illegal transform!");
            return false;
        }


        // Limit the possible values to the sliders.
        let angleAsDegX = THREE.Math.radToDeg(transform.AngleX);
        let angleAsDegY = THREE.Math.radToDeg(transform.AngleY);
        let angleAsDegZ = THREE.Math.radToDeg(transform.AngleZ);

        if (limitToOrientationRanges) {
            transform.PositionX = Math.max(transform.PositionX, this.sliderPosX.rangeMin);
            transform.PositionY = Math.max(transform.PositionY, this.sliderPosY.rangeMin);
            transform.PositionZ = Math.max(transform.PositionZ, this.sliderPosZ.rangeMin);
            angleAsDegX = Math.max(angleAsDegX, this.sliderRotX.rangeMin);
            angleAsDegY = Math.max(angleAsDegY, this.sliderRotY.rangeMin);
            angleAsDegZ = Math.max(angleAsDegZ, this.sliderRotZ.rangeMin);

            transform.PositionX = Math.min(transform.PositionX, this.sliderPosX.rangeMax);
            transform.PositionY = Math.min(transform.PositionY, this.sliderPosY.rangeMax);
            transform.PositionZ = Math.min(transform.PositionZ, this.sliderPosZ.rangeMax);
            angleAsDegX = Math.min(angleAsDegX, this.sliderRotX.rangeMax);
            angleAsDegY = Math.min(angleAsDegY, this.sliderRotY.rangeMax);
            angleAsDegZ = Math.min(angleAsDegZ, this.sliderRotZ.rangeMax);

            // Write back into the mesh...
            this.siteManagerView.siteView.updateMeshPositionX(meshObject.meshInfo.Id, transform.PositionX);
            this.siteManagerView.siteView.updateMeshPositionY(meshObject.meshInfo.Id, transform.PositionY);
            this.siteManagerView.siteView.updateMeshPositionZ(meshObject.meshInfo.Id, transform.PositionZ);
            this.siteManagerView.siteView.updateMeshRotationX(meshObject.meshInfo.Id, angleAsDegX);
            this.siteManagerView.siteView.updateMeshRotationY(meshObject.meshInfo.Id, angleAsDegY);
            this.siteManagerView.siteView.updateMeshRotationZ(meshObject.meshInfo.Id, angleAsDegZ);
        }

        return [
            this.sliderPosX.setValue(transform.PositionX, Localization.formatNumber3Digits),
            this.sliderPosY.setValue(transform.PositionY, Localization.formatNumber3Digits),
            this.sliderPosZ.setValue(transform.PositionZ, Localization.formatNumber3Digits),
            this.sliderRotX.setValue(angleAsDegX, Localization.formatNumber3Digits),
            this.sliderRotY.setValue(angleAsDegY, Localization.formatNumber3Digits),
            this.sliderRotZ.setValue(angleAsDegZ, Localization.formatNumber3Digits)
        ].reduce((prev, curr) => prev || curr, false);
    }
}
